import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { scrollToTop } from "../../../utils/navigation";
import { MessageCircle, ArrowLeft } from "lucide-react";
import Dots from "../../ElecCoopLandingPage/Dots";

const InvoicingChecker = () => {
  const navigate = useNavigate();
  const handleCall = () => {
    scrollToTop();
    navigate("/signup");
  };
  return (
    <AnimatePresence>
      <motion.div
        className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 text-customText-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}>
        <motion.header
          className="w-full px-8 py-8 text-center bg-blue-400 shadow-lg text-customText-100"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}>
          <h1 className="text-4xl font-extrabold tracking-wide">
            BETH AI Module: Invoicing Checker Bot
          </h1>
          <p className="max-w-3xl mx-auto mt-3 text-lg">
            Effortless Validation, Hassle-Free Invoicing, Analyzed and Reviewed
            by AI.
          </p>
        </motion.header>
        <main className="flex flex-col items-center w-full px-6 mt-12 space-y-16">
          <motion.section
            className="flex flex-col items-center space-y-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}>
            <h2 className="flex items-center justify-center text-3xl font-semibold text-customText-800">
              Time-Saving, Accurate Validation, Reduced Risks
            </h2>
            <p className="max-w-5xl mx-auto leading-relaxed text-gray-600">
              The Invoicing Requirement Checker Bot helps businesses,
              accountants, and finance teams ensure that every invoice complies
              with BIR invoicing regulations. By simply uploading invoices—up to
              10 at a time—the bot instantly reviews, validates, and highlights
              compliance issues. It minimizes the risk of penalties, disallowed
              VAT claims, and refund denials, while giving businesses confidence
              that their documentation is audit-ready. Ultimately, this tool
              lets you focus on running your business while the bot handles the
              complex compliance checks.
            </p>
            <motion.button
              className="flex items-center justify-between gap-4 px-8 py-3 mt-6 font-semibold transition duration-300 transform bg-blue-400 rounded-full shadow-lg text-customText-100 hover:bg-blue-500 hover:scale-105"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCall()}>
              <span>Try it now!</span>
              <MessageCircle className="size-5 " />
            </motion.button>
          </motion.section>

          <motion.div className="flex flex-col w-full ">
            <section className="w-full px-24 space-y-12 text-center">
              <h3 className="text-2xl font-semibold text-gray-700">
                Key Features
              </h3>
              <div className="grid justify-center grid-cols-3 gap-y-4 gap-x-6">
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    Multiple File Upload
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Upload and review up to 10 invoices at once—perfect for
                    batch processing and bulk validation.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    Automated Compliance Review
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Cross-checks invoices against legal and regulatory invoicing
                    requirements under the NIRC, RR No. 7-2024, and related BIR
                    issuances.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    Detailed Compliance Report
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Structured table output showing criteria, compliance status,
                    remarks, and suggested corrective actions.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    Penalty Risk Simulation
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Identifies possible fines, surcharges, and refund
                    disqualification risks from non-compliance.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    Refund Eligibility Verification
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Confirms if invoices meet strict requirements for VAT refund
                    claims.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-blue-700">
                    POS/CRM Compliance Check
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Checks PTU details, serial numbers, and system compliance
                    under BIR rules.
                  </p>
                </motion.div>
              </div>
            </section>
            <h1 className="mt-32 text-2xl font-bold text-center">
              Usage Benefits
            </h1>
            <div className="w-full px-40">
              <table className="w-full mt-6 text-left border-collapse">
                <thead className="bg-blue-300">
                  <tr>
                    <th className="px-5 py-3 font-bold rounded-tl-lg text-customText-900">
                      Benefits
                    </th>
                    <th className="px-5 py-3 font-bold rounded-tr-lg text-customText-900 ">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm shadow-md bg-blue-50">
                  <tr>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Saves Time
                    </td>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Eliminates manual review of invoices by automating
                      compliance checking.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Improves Accuracy
                    </td>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Ensures every invoice is validated against the latest tax
                      rules and standards.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Reduces Risk
                    </td>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Helps businesses avoid penalties, surcharges, and
                      disallowed VAT claims due to non-compliance.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Audit Readiness
                    </td>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Strengthens documentation and compliance trail for tax
                      audits and refund applications.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Actionable Guidance
                    </td>
                    <td className="px-5 py-3 border-b border-blue-400">
                      Provides clear corrective steps for non-compliant
                      invoices.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 rounded-bl-lg">
                      Scalable for Businesses
                    </td>
                    <td className="px-5 py-3 rounded-br-lg">
                      Suitable for SMEs, corporations, and accounting
                      professionals managing multiple transactions.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h1 className="mt-32 text-2xl font-bold text-center">
              Instructional Video
            </h1>
            <div className="relative group flex w-[70%] self-center justify-center px-8 py-6">
              <video
                src="https://bethaibucket.s3.us-east-1.amazonaws.com/videos/BETH+AI+-+Invoicing+Video+(2).mp4"
                controls
                className="z-10"></video>
              <span className="absolute top-0 left-0 z-10 font-bold transition-transform translate-y-10 opacity-0 text-green-950 translate-x-14 group-hover:opacity-100">
                Invoicing Bot Video Demonstration / Presentation
              </span>
              <Dots classname="absolute top-0 right-0 translate-x-1/5 -translate-y-1/5" />
              <Dots classname="absolute bottom-0 left-0 -rotate-180 -translate-x-1/5 translate-y-1/5" />
            </div>
          </motion.div>
        </main>
        <aside className="fixed left-6 bottom-6">
          <Link
            to="/features"
            onClick={scrollToTop}
            className="flex items-center p-4 space-x-2 transition duration-300 bg-white rounded-lg shadow-lg hover:bg-gray-100">
            <ArrowLeft className="text-gray-600" />
            <span className="text-gray-600">Back to Features</span>
          </Link>
        </aside>
        <motion.footer
          className="w-full py-8 mt-20 text-center text-customText-800"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}>
          <p className="text-sm">
            © {new Date().getFullYear()} Babylon by I-Lead. All rights reserved.
          </p>
        </motion.footer>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvoicingChecker;
